/**
 * useTaskStore — Tasks CRUD with subtasks, scheduling, recurrence.
 *
 * Storage: 'tasks' (collection)
 * Entity: { id, title, status, priority, dueDate, subtasks[], projectId, phaseId, properties{}, recurrence }
 * Methods: addTask, updateTask, deleteTask, toggleComplete, addSubtask, toggleSubtask, bulkUpdate
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';
import { localToday, localDateStr } from '../utils/dateUtils';
import { getPlainText } from '../components/ui/DualModeEditor';

/** Coerce a description (TipTap JSON object/string or plain string) to a plain string. */
function normalizeDescription(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    return getPlainText(value);
}

const STORAGE_KEY = 'tasks';

const useTaskStore = create((set, get) => ({
    tasks: storage.get(STORAGE_KEY, []),

    _persist() {
        storage.set(STORAGE_KEY, get().tasks);
    },

    // ==================== CRUD ====================

    addTask(task) {
        const now = new Date().toISOString();
        const newTask = {
            id: crypto.randomUUID(),
            title: task.title || 'Untitled Task',
            description: normalizeDescription(task.description),
            status: task.status || 'todo',
            priority: task.priority || 'medium',

            // Scheduling
            dueDate: task.dueDate || null,
            dueTime: task.dueTime || null,
            startDate: task.startDate || null,
            scheduledDate: task.scheduledDate || null,

            // Estimation & Tracking
            estimatedMinutes: task.estimatedMinutes || null,
            actualMinutes: task.actualMinutes || 0,

            // Organization
            subtasks: task.subtasks || [],
            tags: task.tags || [],
            notes: task.notes || '',
            properties: task.properties || {},

            // Recurrence
            recurrence: task.recurrence || null,

            // Relationships
            projectId: task.projectId || null,
            phaseId: task.phaseId || null,
            identityId: task.identityId || null,
            areaId: task.areaId || null,
            blockedBy: task.blockedBy || [],

            // Metadata
            createdAt: now,
            updatedAt: now,
            completedAt: null,
            sortOrder: task.sortOrder ?? get().tasks.length,
        };
        set(state => ({ tasks: [...state.tasks, newTask] }));
        get()._persist();
        return newTask;
    },

    updateTask(taskId, updates) {
        const safeUpdates = 'description' in updates
            ? { ...updates, description: normalizeDescription(updates.description) }
            : updates;
        set(state => ({
            tasks: state.tasks.map(t =>
                t.id === taskId
                    ? { ...t, ...safeUpdates, updatedAt: new Date().toISOString() }
                    : t
            ),
        }));
        get()._persist();
    },

    deleteTask(taskId) {
        set(state => ({
            tasks: state.tasks.filter(t => t.id !== taskId),
        }));
        get()._persist();
    },

    completeTask(taskId) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;

        const now = new Date().toISOString();

        // If recurring, spawn next instance instead of completing
        if (task.recurrence) {
            const nextDate = get()._getNextRecurrenceDate(task);
            if (nextDate) {
                // Create next occurrence
                get().addTask({
                    ...task,
                    id: undefined, // Will be regenerated
                    status: 'todo',
                    scheduledDate: nextDate,
                    dueDate: nextDate,
                    completedAt: null,
                    actualMinutes: 0,
                    subtasks: task.subtasks.map(s => ({ ...s, completed: false })),
                });
            }
        }

        // Mark current as done
        get().updateTask(taskId, {
            status: 'done',
            completedAt: now,
        });
    },

    uncompleteTask(taskId) {
        get().updateTask(taskId, {
            status: 'todo',
            completedAt: null,
        });
    },

    clearPhase(phaseId) {
        set(state => ({
            tasks: state.tasks.map(t =>
                t.phaseId === phaseId ? { ...t, phaseId: null, updatedAt: new Date().toISOString() } : t
            ),
        }));
        get()._persist();
    },

    // ==================== SUBTASK CRUD ====================

    addSubtask(taskId, subtask) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;
        const newSubtask = {
            id: crypto.randomUUID(),
            title: subtask.title || '',
            completed: false,
            dueDate: subtask.dueDate || null,
        };
        get().updateTask(taskId, {
            subtasks: [...(task.subtasks || []), newSubtask],
        });
        return newSubtask;
    },

    toggleSubtask(taskId, subtaskId) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;
        get().updateTask(taskId, {
            subtasks: (task.subtasks || []).map(s =>
                s.id === subtaskId ? { ...s, completed: !s.completed } : s
            ),
        });
    },

    deleteSubtask(taskId, subtaskId) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;
        get().updateTask(taskId, {
            subtasks: (task.subtasks || []).filter(s => s.id !== subtaskId),
        });
    },

    updateSubtask(taskId, subtaskId, updates) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;
        get().updateTask(taskId, {
            subtasks: (task.subtasks || []).map(s =>
                s.id === subtaskId ? { ...s, ...updates } : s
            ),
        });
    },

    convertToTask(taskId, subtaskId) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;
        const subtask = (task.subtasks || []).find(s => s.id === subtaskId);
        if (!subtask) return;

        // Create new task
        get().addTask({
            title: subtask.title,
            dueDate: subtask.dueDate || task.dueDate || null,
            projectId: task.projectId || null,
            areaId: task.areaId || null,
            phaseId: task.phaseId || null,
            priority: task.priority,
            description: `Converted from subtask of: ${task.title}`
        });

        // Remove from subtasks
        get().deleteSubtask(taskId, subtaskId);
    },

    // ==================== SCHEDULING ====================

    scheduleTask(taskId, date) {
        get().updateTask(taskId, { scheduledDate: date });
    },

    rescheduleTask(taskId, newDate) {
        get().updateTask(taskId, { scheduledDate: newDate, dueDate: newDate });
    },

    snoozeTask(taskId) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const dateStr = localDateStr(tomorrow);
        get().updateTask(taskId, { scheduledDate: dateStr });
    },

    // ==================== TIME TRACKING ====================

    addActualMinutes(taskId, minutes) {
        const task = get().tasks.find(t => t.id === taskId);
        if (!task) return;
        get().updateTask(taskId, {
            actualMinutes: (task.actualMinutes || 0) + minutes,
        });
        // Auto-move to in-progress if still todo
        if (task.status === 'todo') {
            get().updateTask(taskId, { status: 'in-progress' });
        }
    },

    // ==================== ORDERING ====================

    reorderTasks(taskIds) {
        set(state => ({
            tasks: state.tasks.map(t => {
                const idx = taskIds.indexOf(t.id);
                return idx >= 0 ? { ...t, sortOrder: idx } : t;
            }),
        }));
        get()._persist();
    },

    // ==================== QUERIES ====================

    getTasksForToday() {
        const today = localToday();
        return get().tasks.filter(t => {
            if (t.status === 'done' || t.status === 'cancelled') return false;
            return (
                t.scheduledDate === today ||
                t.dueDate === today ||
                (t.dueDate && t.dueDate < today) // overdue
            );
        });
    },

    getTodayTasks() {
        const today = localToday();
        return get().tasks.filter(t => {
            if (t.status === 'done' || t.status === 'cancelled') return false;
            return t.scheduledDate === today || t.dueDate === today;
        });
    },

    getOverdueTasks() {
        const today = localToday();
        return get().tasks.filter(t => {
            if (t.status === 'done' || t.status === 'cancelled') return false;
            return t.dueDate && t.dueDate < today;
        });
    },

    getTasksByProject(projectId) {
        return get().tasks.filter(t => t.projectId === projectId);
    },

    getTasksByArea(areaId) {
        return get().tasks.filter(t => t.areaId === areaId);
    },

    getInboxTasks() {
        return get().tasks.filter(t => {
            if (t.status === 'done' || t.status === 'cancelled') return false;
            return !t.projectId && !t.areaId && !t.scheduledDate;
        });
    },

    getUpcomingTasks(days = 7) {
        const today = new Date();
        const end = new Date();
        end.setDate(today.getDate() + days);
        const todayStr = localDateStr(today);
        const endStr = localDateStr(end);
        return get().tasks.filter(t => {
            if (t.status === 'done' || t.status === 'cancelled') return false;
            const d = t.dueDate || t.scheduledDate;
            return d && d >= todayStr && d <= endStr;
        });
    },

    // ==================== RECURRENCE HELPER ====================

    _getNextRecurrenceDate(task) {
        if (!task.recurrence) return null;
        const { frequency, interval, endDate } = task.recurrence;
        const base = task.dueDate ? new Date(task.dueDate) : new Date();
        let next = new Date(base);

        switch (frequency) {
            case 'daily':
                next.setDate(next.getDate() + (interval || 1));
                break;
            case 'weekly':
                next.setDate(next.getDate() + 7 * (interval || 1));
                break;
            case 'monthly':
                next.setMonth(next.getMonth() + (interval || 1));
                break;
            default:
                return null;
        }

        if (endDate && localDateStr(next) > endDate) return null;

        return localDateStr(next);
    },
}));

// Re-read from localStorage after cloud sync completes
onRehydrate(() => {
    useTaskStore.setState({ tasks: storage.get(STORAGE_KEY, []) });
});

export default useTaskStore;
